import { CSSProperties } from 'react';

// Shared by Select/AutocompleteSelect/Autocomplete/SelectMultiple/EmployeeSearch's
// portaled dropdown lists: position/size come from a live `getBoundingClientRect()`
// measurement of the trigger and must stay inline; `maxWidth`/`zIndex` are
// constant and belong in className.
//
// The menu is clamped to the space actually available rather than a fixed height,
// which is what lets it work inside a short dialog or on a small viewport. It
// flips above the trigger only when below is genuinely unusable.

const MARGIN = 8;          // breathing room from the viewport edge
const GAP = 4;             // gap between trigger and menu
const DEFAULT_MAX_H = 288; // 18rem - the height every caller used to hardcode as `max-h-72`
const MIN_USABLE_H = 120;  // below this, dropping down isn't worth it if up is roomier

export function getPopoverPositionStyle(
  rect: DOMRect,
  opts?: { maxHeight?: number },
): { style: CSSProperties, className: string } {
  const desired = opts?.maxHeight ?? DEFAULT_MAX_H;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const spaceBelow = vh - rect.bottom - GAP - MARGIN;
  const spaceAbove = rect.top - GAP - MARGIN;
  const flip = spaceBelow < Math.min(desired, MIN_USABLE_H) && spaceAbove > spaceBelow;
  const available = flip ? spaceAbove : spaceBelow;

  // Keep the menu on screen horizontally. It is at least as wide as the trigger,
  // so shift it left rather than letting it run past the right edge.
  const width = Math.min(rect.width, vw - MARGIN * 2);
  const left = Math.min(Math.max(rect.left, MARGIN), Math.max(MARGIN, vw - width - MARGIN));

  return {
    style: {
      position: 'fixed',
      left,
      minWidth: width,
      maxHeight: Math.max(Math.min(desired, available), 0),
      // Anchoring the flipped case with `bottom` avoids needing the menu's own height.
      ...(flip ? { bottom: vh - rect.top + GAP } : { top: rect.bottom + GAP }),
    },
    className: 'max-w-[90vw] z-[1400]',
  };
}
